<script setup lang="ts">
import { format } from "date-fns"
import { useForm } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { useToast } from "@/components/ui/toast/use-toast"
import { links, types, employees, branches } from "@/store"

const { toast } = useToast()
const wantsToCreateMore = ref(false)
const open = ref(false)
const $name = ref()
const branchEmployees = computed(() => employees.value.filter((employee) => employee.branch_id == form.branch_id || employee.branch_id == null))
const form = useForm({
    branch_id: branches.value[0]?.id.toString() ?? "",
    type_id: types.value[0]?.id.toString() ?? "",
    name: "",
    amount: "",
    employee_id: "",
    date: format(new Date(), "yyyy-MM-dd"),
})

watch(() => form.branch_id, () => {
    if (!branchEmployees.value.some((employee) => employee.id.toString() === form.employee_id)) {
        form.employee_id = ""
    }
})

watch(() => [form.type_id, form.employee_id], () => {
    if (form.type_id === "6") {
        const employee = branchEmployees.value.find((employee) => employee.id.toString() === form.employee_id)
        if (employee) form.name = employee.name
    }
})

function submit() {
    form.transform((data) => ({ ...data, employee_id: data.type_id === "6" ? data.employee_id : null })).post(links.value.transactions.store_path, {
        preserveScroll: true,
        onSuccess() {
            if (!wantsToCreateMore.value) {
                form.reset(), (open.value = false)
            } else {
                form.name = ""
                form.amount = ""
                form.employee_id = ""

                $name.value?.$el?.focus()
            }

            toast({
                description: "Transaction successfully added!",
            })
        },
    })
}
</script>

<template>
    <Dialog v-model:open="open">
        <DialogTrigger as-child>
            <Button> Add transaction </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-[625px]">
            <form :action="links.transactions.store_path" @submit.prevent="submit" method="POST">
                <input type="hidden" name="_token" value="" />

                <DialogHeader>
                    <DialogTitle>New Transaction</DialogTitle>
                    <DialogDescription> Record the branch, type, amount, and date of your transaction. </DialogDescription>
                </DialogHeader>
                <div class="grid gap-4 py-4">
                    <div class="flex space-x-4">
                        <FormField name="branch">
                            <FormItem :error="form.errors.branch_id">
                                <FormLabel>Branch</FormLabel>
                                <FormControl>
                                    <Select v-model="form.branch_id">
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a branch" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectItem v-for="(option, index) in branches" :key="index" :value="option.id.toString()">
                                                    {{ option.name }}
                                                </SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>

                        <FormField name="type">
                            <FormItem :error="form.errors.type_id">
                                <FormLabel>Type</FormLabel>
                                <FormControl>
                                    <Select v-model="form.type_id">
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectItem v-for="(option, index) in types" :key="index" :value="option.id.toString()">
                                                    {{ option.name }}
                                                </SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>

                    <div class="grid gap-2">
                        <FormField name="employee" v-if="form.type_id == '6'">
                            <FormItem :error="form.errors.employee_id">
                                <FormLabel>Employee</FormLabel>
                                <FormControl>
                                    <Select v-model="form.employee_id">
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectItem v-for="(option, index) in branchEmployees" :key="index" :value="option.id.toString()">
                                                    {{ option.name }}
                                                </SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>

                        <FormField name="name">
                            <FormItem :error="form.errors.name">
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                    <Input v-model="form.name" ref="$name" required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>

                        <FormField name="amount">
                            <FormItem :error="form.errors.amount">
                                <FormLabel>Amount</FormLabel>
                                <FormControl>
                                    <Input type="number" min="0" step="0.01" v-model="form.amount" required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>
                </div>
                <Separator class="my-6" />
                <DialogFooter class="flex sm:justify-between">
                    <FormField name="date">
                        <FormItem :error="form.errors.date">
                            <FormLabel>Date</FormLabel>
                            <FormControl>
                                <Input type="date" v-model="form.date" required />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>

                    <div class="[ flex gap-2 items-center ml-auto ]">
                        <label
                            class="[ font-medium px-4 py-2 rounded-md text-xs flex items-center gap-1 select-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-canvas transition duration-100 h-10 ]"
                            :class="{
                                '[ bg-neutral-100/50 hover:bg-neutral-100 focus-within:ring-stroke ]': !wantsToCreateMore,
                                '[ bg-blue-100 text-blue-900 focus-within:ring-accent-base ]': wantsToCreateMore,
                            }"
                        >
                            <input type="checkbox" name="create_more" value="1" v-model="wantsToCreateMore" class="[ sr-only ]" />

                            <div
                                class="[ h-2 w-2 rounded-full ]"
                                :class="{
                                    '[ bg-blue-400 shadow-accent-base shadow-xl ]': wantsToCreateMore,
                                    '[ bg-neutral-400 ]': !wantsToCreateMore,
                                }"
                            ></div>

                            <span class="[ ms-0.5 ]">Add more</span>
                        </label>

                        <Button type="submit" :disabled="form.processing">{{ form.processing ? "Saving…" : "Save" }}</Button>
                    </div>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
